
import React, { useState, useRef } from 'react';
import * as api from '../utils/crmApi';

const ImportModal = ({ isOpen, onClose, entity, onImported }) => {
    const [step, setStep] = useState('upload'); // upload, map, processing, result
    const [file, setFile] = useState(null);
    const [csvText, setCsvText] = useState('');
    const [headers, setHeaders] = useState([]);
    const [previewRows, setPreviewRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [options, setOptions] = useState({
        mode: 'create_only',
        dryRun: true,
        defaultStatus: 'new',
        assignToSelf: true
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fileInputRef = useRef(null);

    // Reset state when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setStep('upload');
            setFile(null);
            setCsvText('');
            setHeaders([]);
            setPreviewRows([]);
            setMapping({});
            setResult(null);
            setError(null);
            setOptions(prev => ({ ...prev, dryRun: true }));
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const CRM_FIELDS = entity === 'leads' ? [
        { key: 'firstName', label: 'First Name' },
        { key: 'lastName', label: 'Last Name' },
        { key: 'name', label: 'Full Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'company', label: 'Company' },
        { key: 'jobTitle', label: 'Job Title' },
        { key: 'status', label: 'Status' },
        { key: 'source', label: 'Source' },
        { key: 'score', label: 'Score' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'country', label: 'Country' },
        { key: 'tags', label: 'Tags' },
        { key: 'notes', label: 'Notes' },
    ] : [
        { key: 'firstName', label: 'First Name' },
        { key: 'lastName', label: 'Last Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'company', label: 'Company' },
        { key: 'title', label: 'Title' },
        { key: 'department', label: 'Department' },
        { key: 'type', label: 'Type' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'country', label: 'Country' },
        { key: 'tags', label: 'Tags' },
        { key: 'notes', label: 'Notes' },
    ];

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (f) {
            setFile(f);
            const reader = new FileReader();
            reader.onload = (evt) => {
                const text = evt.target.result;
                setCsvText(text);
                parsePreview(text);
            };
            reader.readAsText(f);
        }
    };

    const parsePreview = (text) => {
        // Simple preview parser (assuming standard CSV)
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        if (lines.length > 0) {
            const head = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
            setHeaders(head);

            const rows = lines.slice(1, 6).map(line => {
                // Handle quoted commas strictly? For preview simple split usually ok or reuse robust logic if needed
                // For preview we'll do simple split
                return line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
            });
            setPreviewRows(rows);

            // Auto mapping
            const initialMap = {};
            head.forEach(h => {
                const norm = h.toLowerCase().replace(/[^a-z]/g, '');
                const found = CRM_FIELDS.find(f => {
                    const fNorm = f.label.toLowerCase().replace(/[^a-z]/g, '');
                    return norm === fNorm || norm === f.key.toLowerCase();
                });
                if (found) {
                    initialMap[h] = found.key;
                }
            });
            setMapping(initialMap);
            setStep('map');
        }
    };

    const handleImport = async (isDryRun) => {
        setLoading(true);
        setError(null);
        try {
            const payload = {
                csvText,
                mapping,
                options: { ...options, dryRun: isDryRun }
            };

            const func = entity === 'leads' ? api.importLeads : api.importContacts;
            const res = await func(payload);

            setResult(res);
            if (!isDryRun && res.success && (res.summary.created + res.summary.updated > 0)) {
                if (onImported) onImported();
            }
            if (isDryRun) {
                setOptions(prev => ({ ...prev, dryRun: false })); // Prepare for real run
            } else {
                setStep('result');
            }
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="crm-modal-overlay" onClick={onClose}>
            <div
                className="crm-modal"
                onClick={e => e.stopPropagation()}
                style={{
                    maxWidth: '900px',
                    width: '90%',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    // Force theme compliance
                    background: 'var(--crm-bg)',
                    color: 'var(--crm-gray-900)'
                }}
            >
                <div className="crm-modal-header">
                    <h2 className="crm-modal-title" style={{ color: 'var(--crm-gray-900)' }}>
                        Import {entity === 'leads' ? 'Leads' : 'Contacts'}
                    </h2>
                    <button className="crm-modal-close" onClick={onClose}>&times;</button>
                </div>

                <div className="crm-modal-body" style={{ overflowY: 'auto' }}>
                    {error && (
                        <div className="crm-alert crm-alert-danger" style={{
                            padding: '12px', background: 'var(--crm-danger-light)',
                            color: 'var(--crm-danger)', marginBottom: '16px', border: '1px solid var(--crm-danger)'
                        }}>
                            {error}
                        </div>
                    )}

                    {step === 'upload' && (
                        <div className="crm-form-group">
                            <div
                                style={{
                                    textAlign: 'center', padding: '48px',
                                    border: '2px dashed var(--crm-gray-300)',
                                    borderRadius: 'var(--crm-border-radius)',
                                    background: 'var(--crm-bg-secondary)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => fileInputRef.current.click()}
                            >
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv" style={{ display: 'none' }} />
                                <div style={{ fontSize: '24px', marginBottom: '8px', color: 'var(--crm-gray-400)' }}>📄</div>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: 'var(--crm-gray-800)' }}>Click to upload CSV</h3>
                                <p style={{ margin: 0, color: 'var(--crm-gray-500)', fontSize: '13px' }}>or drag and drop here</p>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '16px', color: 'var(--crm-gray-400)', fontSize: '12px' }}>
                                Supported format: Generic CSV with header row
                            </div>
                        </div>
                    )}

                    {step === 'map' && (
                        <div>
                            <div className="crm-form-row" style={{ marginBottom: '24px' }}>
                                <div className="crm-form-group">
                                    <label className="crm-label" style={{ color: 'var(--crm-gray-700)' }}>Import Mode</label>
                                    <select
                                        className="crm-select"
                                        value={options.mode}
                                        onChange={e => setOptions({ ...options, mode: e.target.value })}
                                        style={{ background: 'var(--crm-bg)', color: 'var(--crm-gray-900)' }}
                                    >
                                        <option value="create_only">Create Only (Skip duplicates)</option>
                                        <option value="upsert_by_email">Update by Email</option>
                                        <option value="upsert_by_phone">Update by Phone</option>
                                    </select>
                                </div>
                                {entity === 'leads' && (
                                    <div className="crm-form-group">
                                        <label className="crm-label" style={{ color: 'var(--crm-gray-700)' }}>Default Status</label>
                                        <select
                                            className="crm-select"
                                            value={options.defaultStatus}
                                            onChange={e => setOptions({ ...options, defaultStatus: e.target.value })}
                                            style={{ background: 'var(--crm-bg)', color: 'var(--crm-gray-900)' }}
                                        >
                                            <option value="new">New</option>
                                            <option value="contacted">Contacted</option>
                                            <option value="qualified">Qualified</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            <h3 className="crm-card-title" style={{ marginBottom: '16px', color: 'var(--crm-gray-900)' }}>Map CSV Columns</h3>
                            <div className="crm-table-container" style={{ border: '1px solid var(--crm-gray-200)', maxHeight: '400px', overflowY: 'auto' }}>
                                <table className="crm-table">
                                    <thead>
                                        <tr>
                                            <th>CSV Header</th>
                                            <th>Preview (Row 1)</th>
                                            <th>CRM Field</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {headers.map((h, i) => (
                                            <tr key={i}>
                                                <td className="cell-main" style={{ color: 'var(--crm-gray-900)' }}>{h}</td>
                                                <td className="cell-secondary" style={{ color: 'var(--crm-gray-500)' }}>{previewRows[0]?.[i]}</td>
                                                <td>
                                                    <select
                                                        className="crm-select crm-select-sm"
                                                        value={mapping[h] || ''}
                                                        onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                                                        style={{ background: 'var(--crm-bg)', color: 'var(--crm-gray-900)' }}
                                                    >
                                                        <option value="">-- Ignore --</option>
                                                        {CRM_FIELDS.map(f => (
                                                            <option key={f.key} value={f.key}>{f.label}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="crm-form-actions" style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                <button className="crm-btn crm-btn-secondary" onClick={() => setStep('upload')}>Back</button>
                                <button className="crm-btn crm-btn-secondary" onClick={() => handleImport(true)} disabled={loading}>
                                    {loading ? 'Validating...' : 'Validate (Dry Run)'}
                                </button>
                                {result && result.success && (
                                    <button className="crm-btn crm-btn-primary" onClick={() => handleImport(false)} disabled={loading}>
                                        {loading ? 'Importing...' : `Import ${result.summary.totalRows} Rows`}
                                    </button>
                                )}
                            </div>

                            {result && (
                                <div style={{ marginTop: '24px', padding: '16px', background: 'var(--crm-bg-secondary)', border: '1px solid var(--crm-gray-200)', borderRadius: 'var(--crm-border-radius)' }}>
                                    <h4 className="crm-card-title" style={{ fontSize: '14px', marginBottom: '8px', color: 'var(--crm-gray-900)' }}>Validation Results</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
                                        <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--crm-gray-900)' }}>{result.summary.totalRows}</div><div style={{ fontSize: '12px', color: 'var(--crm-gray-500)' }}>Total</div></div>
                                        <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--crm-success)' }}>{result.summary.created}</div><div style={{ fontSize: '12px', color: 'var(--crm-gray-500)' }}>New</div></div>
                                        <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--crm-primary)' }}>{result.summary.updated}</div><div style={{ fontSize: '12px', color: 'var(--crm-gray-500)' }}>Update</div></div>
                                        <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--crm-danger)' }}>{result.summary.errorsCount}</div><div style={{ fontSize: '12px', color: 'var(--crm-gray-500)' }}>Errors</div></div>
                                    </div>
                                    {result.errors.length > 0 && (
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '13px', borderTop: '1px solid var(--crm-gray-200)', paddingTop: '8px' }}>
                                            {result.errors.map((e, idx) => (
                                                <div key={idx} style={{ color: 'var(--crm-danger)', marginBottom: '4px' }}>
                                                    Row {e.rowNumber}: {e.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {step === 'result' && result && (
                        <div style={{ textAlign: 'center', padding: '32px 0' }}>
                            <div style={{ fontSize: '48px', marginBottom: '8px' }}>✅</div>
                            <h3 className="crm-page-title" style={{ justifyContent: 'center', marginBottom: '8px', color: 'var(--crm-gray-900)' }}>Import Complete</h3>
                            <p style={{ color: 'var(--crm-gray-600)', marginBottom: '32px' }}>
                                Successfully processed {result.summary.totalRows} rows.
                            </p>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: '400px', margin: '0 auto 32px auto', gap: '24px' }}>
                                <div>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--crm-success)' }}>{result.summary.created}</div>
                                    <div style={{ fontSize: '13px', color: 'var(--crm-gray-500)' }}>Created</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--crm-primary)' }}>{result.summary.updated}</div>
                                    <div style={{ fontSize: '13px', color: 'var(--crm-gray-500)' }}>Updated</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--crm-danger)' }}>{result.summary.errorsCount}</div>
                                    <div style={{ fontSize: '13px', color: 'var(--crm-gray-500)' }}>Errors</div>
                                </div>
                            </div>

                            <button className="crm-btn crm-btn-primary" onClick={onClose}>Done</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportModal;
